import React from "react";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Tooltip,
} from "@mui/material";
import { useLocation } from "react-router";

import {
  DashboardIcon,
  OrganizationIcon,
  ProjectIcon,
  ConnectionIcon,
} from "@shell/components/icons";
import {
  ACCOUNT,
  DASHBOARD,
  ORGANIZATIONS,
} from "src/platform/constants/routes";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { ALPHA } from "src/shell/styles/designTokens";

import { SidebarNavigationProps } from "../types/NavigationTypes";

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  matchFn?: (p: string) => boolean;
  onClick: () => void;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  collapsed = false,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias: activeOrgAlias } = useActiveOrganization();

  const isActive = (path: string, matchFn?: (p: string) => boolean) => {
    if (matchFn) return matchFn(location.pathname);
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const getItemStyles = (active: boolean) => {
    const base = {
      color: active ? theme.palette.primary.main : theme.palette.text.secondary,
      backgroundColor: active
        ? alpha(theme.palette.primary.main, ALPHA.NAV_ACTIVE_BG)
        : "transparent",
      height: 40,
      mb: "4px",
      position: "relative" as const,
      borderRadius: "8px",
      "&:hover": {
        backgroundColor: active
          ? alpha(theme.palette.primary.main, ALPHA.NAV_ACTIVE_HOVER)
          : alpha(theme.palette.primary.main, 0.04),
      },
      "&::before": active
        ? {
            content: '""',
            position: "absolute",
            left: collapsed ? -2 : -12,
            top: "50%",
            transform: "translateY(-50%)",
            width: 3,
            height: 20,
            borderRadius: "0 3px 3px 0",
            backgroundColor: theme.palette.primary.main,
          }
        : {},
      transition: "background-color 150ms ease",
    };

    if (collapsed) {
      return {
        ...base,
        justifyContent: "center",
        width: 40,
        height: 40,
        minWidth: 0,
        mx: "auto",
        px: 0,
      };
    }

    return {
      ...base,
      justifyContent: "flex-start",
    };
  };

  const getIconStyles = (active: boolean) => ({
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
    minWidth: collapsed ? 0 : 40,
    justifyContent: "center",
    opacity: active ? 1 : 0.6,
  });

  const primaryItems: NavItem[] = [
    {
      text: "Dashboard",
      icon: <DashboardIcon size={20} />,
      path: DASHBOARD.path,
      onClick: () => appNavigation.goToDashboard(),
    },
    {
      text: "Organizations",
      icon: <OrganizationIcon size={20} />,
      path: ORGANIZATIONS.ROOT.path,
      matchFn: (p: string) =>
        p === ORGANIZATIONS.ROOT.path ||
        (p.startsWith(ORGANIZATIONS.ROOT.path + "/") &&
          !p.includes("/projects")),
      onClick: () => appNavigation.goToOrganizations(),
    },
    {
      text: "Projects",
      icon: <ProjectIcon size={20} />,
      path: ORGANIZATIONS.ROOT.path,
      matchFn: (p: string) => p.includes("/projects"),
      onClick: () => {
        const urlOrgAlias = location.pathname.match(
          /\/organizations\/([^/]+)/,
        )?.[1];
        const org = urlOrgAlias ?? activeOrgAlias;
        if (org) {
          appNavigation.goToProjects(org);
        } else {
          appNavigation.goToOrganizations();
        }
      },
    },
    {
      text: "Connections",
      icon: <ConnectionIcon size={20} />,
      path: ORGANIZATIONS.VCS_CONNECTIONS.path,
      matchFn: (p: string) => p.includes("/connections"),
      onClick: () => {
        const urlOrgAlias = location.pathname.match(
          /\/organizations\/([^/]+)/,
        )?.[1];
        const org = urlOrgAlias ?? activeOrgAlias;
        if (org) {
          appNavigation.goToVcsConnections(org);
        } else {
          appNavigation.goToOrganizations();
        }
      },
    },
  ];

  const accountItems: NavItem[] = [
    {
      text: "Profile",
      icon: <PersonOutlineIcon />,
      path: ACCOUNT.PROFILE.path,
      onClick: () => appNavigation.goToProfile(),
    },
    {
      text: "Settings",
      icon: <SettingsIcon />,
      path: ACCOUNT.SETTINGS.path,
      onClick: () => appNavigation.goToAccountSettings(),
    },
  ];

  const renderItem = (item: NavItem) => {
    const active = isActive(item.path, item.matchFn);
    return (
      <ListItem
        key={item.text}
        disablePadding
        sx={collapsed ? { justifyContent: "center" } : {}}
      >
        <Tooltip title={collapsed ? item.text : ""} placement="right" arrow>
          <ListItemButton onClick={item.onClick} sx={getItemStyles(active)}>
            <ListItemIcon sx={getIconStyles(active)}>{item.icon}</ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Primary navigation */}
      <List
        component="nav"
        sx={{ px: collapsed ? 0 : "12px", pt: 1, mb: "16px" }}
      >
        {primaryItems.map(renderItem)}
      </List>

      {/* Account navigation */}
      <List component="nav" sx={{ px: collapsed ? 0 : "12px", pb: 1 }}>
        {accountItems.map(renderItem)}
      </List>
    </Box>
  );
};

export default SidebarNavigation;
